// src/lib/image-compression.js — PR #153 commit 9
//
// Compresse une image client-side avant upload Storage Supabase.
// Limite bucket logements-photos : 10 MB. On vise 8 MB max après
// compression (marge sécurité 2 MB pour metadata multipart).
//
// Stratégie 3 niveaux :
//   1. Resize si srcWidth > maxWidth (default 2400 ; suffisant preuves
//      visuelles)
//   2. Encode JPEG quality 0.85
//   3. Si > 8 MB après → retry quality 0.7
//   4. Si > 8 MB après → retry quality 0.6
//   5. Si toujours > 8 MB après tout → return le blob le plus petit
//      obtenu + console.warn (best-effort, le caller upload quand même)
//
// No-op si fichier déjà < target (pas de re-encode inutile).
//
// Fallback : si Canvas non dispo (theoretical, ex SSR ou old browser) →
// throw graceful, le caller peut tenter upload original (best-effort).
//
// Pas de dépendance npm : Image() + Canvas natifs browser (bundle
// minimal). Évite browser-image-compression et similaires.

const DEFAULT_MAX_WIDTH = 2400
const DEFAULT_QUALITIES = [0.85, 0.7, 0.6]
const TARGET_SIZE_BYTES = 8 * 1024 * 1024 // 8 MB
const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

/**
 * Compresse une image vers JPEG.
 * Si la photo originale est déjà sous targetSizeBytes → no-op (return original).
 *
 * @param {File|Blob} file
 * @param {object} [options]
 * @param {number} [options.maxWidth=2400]
 * @param {number} [options.targetSizeBytes=8MB]
 * @returns {Promise<{ blob: Blob, originalSize: number, finalSize: number, compressed: boolean }>}
 */
export async function compressImage(file, options = {}) {
  if (!file || !(file instanceof Blob)) {
    throw new Error('compressImage: file invalid (Blob/File required)')
  }
  if (!SUPPORTED_TYPES.includes(file.type)) {
    throw new Error(
      `compressImage: type non supporté (${file.type}). Attendu : ${SUPPORTED_TYPES.join(', ')}`
    )
  }

  const maxWidth = options.maxWidth || DEFAULT_MAX_WIDTH
  const targetSize = options.targetSizeBytes || TARGET_SIZE_BYTES
  const originalSize = file.size

  // No-op si déjà sous target — pas de re-encode inutile
  if (originalSize <= targetSize) {
    return { blob: file, originalSize, finalSize: originalSize, compressed: false }
  }

  // Vérifier disponibilité Canvas (fallback gracieux SSR / browser legacy)
  if (typeof document === 'undefined' || typeof HTMLCanvasElement === 'undefined') {
    throw new Error('compressImage: Canvas API non disponible (SSR ou browser legacy)')
  }

  // Lire l'image dans un HTMLImageElement
  const img = await loadImage(file)
  const { width, height } = computeResizedDimensions(img.width, img.height, maxWidth)

  // Tenter les qualités en série, return dès que < targetSize
  let bestBlob = null
  let bestSize = Infinity
  for (const quality of DEFAULT_QUALITIES) {
    const blob = await renderToBlob(img, width, height, quality)
    if (blob.size < bestSize) {
      bestBlob = blob
      bestSize = blob.size
    }
    if (blob.size <= targetSize) {
      return { blob, originalSize, finalSize: blob.size, compressed: true }
    }
  }

  // Toutes les qualités > target → return le plus petit obtenu (best-effort)
  // eslint-disable-next-line no-console
  console.warn(
    `[compressImage] Target ${targetSize}B non atteint. Best ${bestSize}B (orig ${originalSize}B)`
  )
  return { blob: bestBlob, originalSize, finalSize: bestSize, compressed: true }
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('compressImage: chargement image échoué'))
    }
    img.src = url
  })
}

function computeResizedDimensions(srcW, srcH, maxWidth) {
  if (srcW <= maxWidth) return { width: srcW, height: srcH }
  const ratio = maxWidth / srcW
  return { width: maxWidth, height: Math.round(srcH * ratio) }
}

function renderToBlob(img, width, height, quality) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return reject(new Error('compressImage: 2D context non disponible'))
    ctx.drawImage(img, 0, 0, width, height)
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('compressImage: toBlob retourné null'))),
      'image/jpeg',
      quality
    )
  })
}
